import { Emitter } from '@/shared/lib/emitter';

import type { AuthTokens } from './types';

/**
 * Token custody.
 *
 * The access token lives in a module-scoped variable and NOWHERE else. It is
 * never written to localStorage, never logged, never put in a URL. WalletFront
 * kept it in `localStorage` under `authToken` (and at one point `console.log`-ed
 * it), which hands the token to any XSS that ever lands on the page.
 *
 * The refresh token is the awkward one: it has to survive a page reload, and a
 * pure SPA cannot read an httpOnly cookie. The right answer is for the backend
 * to set the refresh token as an httpOnly, Secure, SameSite=Strict cookie and
 * for us to send `credentials: 'include'` — that mode is supported here via
 * `configureTokenStore({ refreshTransport: 'cookie' })` and should be switched on
 * as soon as the backend does it. Until then we fall back to `sessionStorage`,
 * which at least dies with the tab, unlike `localStorage`.
 */

export type RefreshTransport = 'cookie' | 'session-storage';

interface TokenEvents {
  /** Access token changed (login, refresh, logout). */
  changed: { hasSession: boolean };
  /** The session is gone and the user must authenticate again. */
  expired: { reason: SessionEndReason };
}

/**
 * Why a session ended.
 *
 * `session-revoked` is the important one: the backend answered a refresh with
 * `refresh_token_reuse`, which means an already-rotated refresh token was
 * presented and the whole token family was revoked. Either the user restored an
 * old tab, or someone stole the token — and we cannot tell which. The UI must
 * say so plainly rather than showing a bland "session expired".
 */
export type SessionEndReason = 'refresh-failed' | 'no-refresh-token' | 'session-revoked' | 'logout';

const REFRESH_STORAGE_KEY = 'novix.rt';

let accessToken: string | null = null;
// The refresh token is held in memory as the source of truth and only MIRRORED
// to sessionStorage so it survives a reload. That keeps the app working in
// contexts where storage is blocked (private mode, some embedded webviews)
// instead of silently logging the user out on every navigation.
let refreshToken: string | null = null;
let expiresAt: number | null = null;
let refreshTransport: RefreshTransport = 'session-storage';

export const tokenEvents = new Emitter<TokenEvents>();

const ASCII_PRINTABLE = /^[\x21-\x7E]+$/;

/**
 * A token with a stray newline or non-ASCII byte makes `fetch` throw when it is
 * put in a header, which surfaces as an unhelpful TypeError. WalletFront learned
 * this the hard way; sanitising up front turns it into a clean re-auth.
 */
export function sanitizeToken(token: unknown): string | null {
  if (typeof token !== 'string') return null;
  const trimmed = token.trim();
  if (!trimmed || !ASCII_PRINTABLE.test(trimmed)) return null;
  return trimmed;
}

function readStoredRefreshToken(): string | null {
  if (refreshTransport === 'cookie') return null;
  if (refreshToken !== null) return refreshToken;
  try {
    refreshToken = sanitizeToken(globalThis.sessionStorage.getItem(REFRESH_STORAGE_KEY));
    return refreshToken;
  } catch {
    return null;
  }
}

function writeStoredRefreshToken(token: string | null): void {
  if (refreshTransport === 'cookie') return;
  refreshToken = token;
  try {
    if (token) globalThis.sessionStorage.setItem(REFRESH_STORAGE_KEY, token);
    else globalThis.sessionStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    /* storage disabled — the in-memory copy still serves this session */
  }
}

export function configureTokenStore(options: { refreshTransport?: RefreshTransport }): void {
  if (options.refreshTransport) refreshTransport = options.refreshTransport;
}

export function getRefreshTransport(): RefreshTransport {
  return refreshTransport;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  return readStoredRefreshToken();
}

export function hasSession(): boolean {
  return accessToken !== null;
}

/** True when we hold a token but it is (nearly) expired — refresh preemptively. */
export function isAccessTokenExpired(skewSeconds = 15): boolean {
  if (expiresAt === null) return false;
  return Date.now() / 1000 >= expiresAt - skewSeconds;
}

export function setTokens(tokens: AuthTokens): void {
  accessToken = sanitizeToken(tokens.accessToken);
  expiresAt = tokens.expiresAt ?? null;

  if (tokens.refreshToken !== undefined) {
    writeStoredRefreshToken(sanitizeToken(tokens.refreshToken));
  }

  tokenEvents.emit('changed', { hasSession: accessToken !== null });
}

export function setAccessToken(token: string | null, tokenExpiresAt?: number | null): void {
  accessToken = sanitizeToken(token);
  if (tokenExpiresAt !== undefined) expiresAt = tokenExpiresAt;
  tokenEvents.emit('changed', { hasSession: accessToken !== null });
}

export function clearTokens(reason: SessionEndReason = 'logout'): void {
  accessToken = null;
  expiresAt = null;
  refreshToken = null;
  writeStoredRefreshToken(null);
  tokenEvents.emit('changed', { hasSession: false });
  tokenEvents.emit('expired', { reason });
}
